<template>
  <template-card
    ref="templateCard"
    :partnerId="computedPartnerId"
    :from="[computedPartner]"
    :context="context"
    :to="to"
    :toPhone="toPhone"
    :refs="refs"
    :channel="channel"
    @sent="(v) => $emit('sent', v)"
    :isTicketEnabled="isTicketEnabled"
    :attachmentFileFilters="attachmentFileFilters"
    @isValid="$emit('isValid', $event)"
  />
</template>

<script lang="ts">
import { ITemplateContext } from "@/lib/interfaces/template/templateContext.interface";
import { PartnerEntity } from "@/models/partnerEntity";
import { PartnerModule } from "@/store/modules/partner";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import TemplateCard from "../template/TemplateCard.vue";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";
import { IReference } from "@/models/reference.entity";
import { IPartnerMessage } from "@/models/partner-message.entity";
import { IPhoneMessage } from "@/models/phone-message.entity";
import { PartnerTemplate } from "@/models/partner-template.entity";
import { ConversationChannelEnum } from "@/lib/enum/conversation-channel.enum";
import { IPageFilterElement } from "@/models/page-filter-element.entity";

@Component({
  components: {
    TemplateCard
  }
})
export default class PreselectTemplateCard extends Vue {
  @Prop()
  private templateKey!: string;

  @Prop()
  private language!: LanguageCodeEnum;

  @Prop({ default: () => [] })
  to!: string[];

  /** Numbers offered when the advisor switches the card to an SMS or WhatsApp inbox. */
  @Prop({ default: () => [] })
  toPhone!: string[];

  @Prop({ default: {} })
  context!: ITemplateContext;

  @Prop({ default: () => [] })
  refs!: IReference[];

  @Prop({ default: true })
  isTicketEnabled?: boolean;

  @Prop({ default: () => [] })
  attachmentFileFilters!: IPageFilterElement[];

  @Prop()
  channel?: ConversationChannelEnum;

  /**
   * Partner to load the template from and send as. Defaults to the partner of the current route,
   * which covers every partner-scoped screen. Pass it explicitly where there is no partner in the
   * route (e.g. the admin user administration, where the sender is picked in the dialog).
   */
  @Prop()
  partnerId?: string;

  /**
   * Sender shown in the template card. Defaults to the currently loaded partner; pass it together
   * with `partnerId` so sender and template stay in sync.
   */
  @Prop()
  partner?: PartnerEntity;

  loading = true;

  get computedPartnerId(): string {
    return this.partnerId || this.$route.params.partnerId || PartnerModule.partner._id;
  }

  get computedPartner(): PartnerEntity {
    return this.partner ?? PartnerModule.partner;
  }

  async mounted() {
    await this.loadTemplate();
  }

  @Watch("templateKey")
  @Watch("language")
  @Watch("computedPartnerId")
  async loadTemplate() {
    const templateCard = this.$refs.templateCard as TemplateCard | undefined;

    try {
      this.loading = true;
      templateCard?.setLoading(true);

      if (!this.computedPartnerId) {
        return;
      }

      const template = new PartnerTemplate({
        partnerId: this.computedPartnerId,
        key: this.templateKey
      });
      // Keep the entity's own default when no language was passed, instead of clearing it.
      template.meta.language = this.language ?? template.meta.language;
      await template.fetch();

      if (template) {
        templateCard?.setTemplate(template);
      }
    } catch (e) {
      this.$log.error(e);
    } finally {
      templateCard?.setLoading(false);
      this.loading = false;
    }
  }

  /**
   * Sends the composed message. Resolves to the created messages, or to undefined when the send
   * failed — TemplateCard reports send errors via toast instead of throwing.
   */
  async send(): Promise<IPartnerMessage[] | IPhoneMessage[] | undefined> {
    return (this.$refs.templateCard as TemplateCard).send();
  }

  /** Recipients the advisor actually picked in the composer's own "to" field. */
  get selectedReceivers(): string[] {
    return (this.$refs.templateCard as TemplateCard | undefined)?.selectedReceivers ?? [];
  }
}
</script>
